import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { documentService } from '../services/documentService';
import LoadingSpinner from '../components/LoadingSpinner';

const DocumentListPage = () => {
  const { hasAnyRole } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useRef(null);

  const [filters, setFilters] = useState({
    search: '',
    status: '',
    page: 1,
    limit: 10,
    sortBy: 'createdAt',
    sortOrder: 'DESC',
  });

  const { data: documentsData, isLoading } = useQuery({
    queryKey: ['documents', filters],
    queryFn: () => documentService.getDocuments(filters),
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }) => documentService.updateDocumentStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries(['documents']);
      toast.current.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Document status updated successfully!',
      });
    },
    onError: (error) => {
      toast.current.show({
        severity: 'error',
        summary: 'Update Failed',
        detail: error.message,
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: documentService.deleteDocument,
    onSuccess: () => {
      queryClient.invalidateQueries(['documents']);
      toast.current.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Document deleted successfully!',
      });
    },
    onError: (error) => {
      toast.current.show({
        severity: 'error',
        summary: 'Delete Failed',
        detail: error.message,
      });
    },
  });

  const statusOptions = [
    { label: 'All Statuses', value: '' },
    { label: 'Draft', value: 'DRAFT' },
    { label: 'Published', value: 'PUBLISHED' },
    { label: 'Revoked', value: 'REVOKED' },
    { label: 'Archived', value: 'ARCHIVED' },
  ];

  const getStatusSeverity = (status) => {
    switch (status) {
      case 'PUBLISHED':
        return 'success';
      case 'DRAFT':
        return 'info';
      case 'REVOKED':
        return 'danger';
      case 'ARCHIVED':
        return 'warning';
      default:
        return 'info';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1, // Reset to first page when filtering
    }));
  };

  const handlePageChange = (event) => {
    setFilters(prev => ({
      ...prev,
      page: event.page + 1,
      limit: event.rows,
    }));
  };

  const handleSort = (event) => {
    setFilters(prev => ({
      ...prev,
      sortBy: event.sortField,
      sortOrder: event.sortOrder === 1 ? 'ASC' : 'DESC',
    }));
  };

  const handleStatusChange = (rowData, newStatus) => {
    updateStatusMutation.mutate({
      id: rowData.id,
      status: newStatus,
    });
  };

  const handleDelete = (rowData) => {
    deleteMutation.mutate(rowData.id);
  };

  const downloadQRCode = async (rowData, format = 'png') => {
    try {
      const blob = await documentService.downloadQRCode(rowData.id, format);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `qr_${rowData.shortCode}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.current.show({
        severity: 'error',
        summary: 'Download Failed',
        detail: error.message,
      });
    }
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="flex gap-1">
        <Button
          icon="pi pi-eye"
          className="p-button-text p-button-sm"
          tooltip="View Details"
          // onClick={() => navigate(`/documents/${rowData.id}`)}
          onClick={() => navigate(`/verify?code=${rowData.shortCode}`)}

        />
        {hasAnyRole(['ADMIN', 'EDITOR']) && (
          <>
            <Button
              icon="pi pi-download"
              className="p-button-text p-button-sm"
              tooltip="Download QR Code"
              onClick={() => downloadQRCode(rowData)}
            />
            {rowData.status === 'DRAFT' && (
              <Button
                icon="pi pi-check"
                className="p-button-text p-button-sm p-button-success"
                tooltip="Publish"
                onClick={() => handleStatusChange(rowData, 'PUBLISHED')}
              />
            )}
            {rowData.status === 'PUBLISHED' && (
              <Button
                icon="pi pi-ban"
                className="p-button-text p-button-sm p-button-warning"
                tooltip="Revoke"
                onClick={() => handleStatusChange(rowData, 'REVOKED')}
              />
            )}
          </>
        )}
        {hasAnyRole(['ADMIN']) && (
          <Button
            icon="pi pi-trash"
            className="p-button-text p-button-sm p-button-danger"
            tooltip="Delete"
            onClick={() => handleDelete(rowData)}
          />
        )}
      </div>
    );
  };

  const statusBodyTemplate = (rowData) => {
    return (
      <Tag
        value={rowData.status}
        severity={getStatusSeverity(rowData.status)}
      />
    );
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading documents..." />;
  }

  const documents = documentsData?.documents || [];
  const pagination = documentsData?.pagination || {};

  return (
    <div className="grid">
      <div className="col-12">
        <Card>
          <div className="flex align-items-center justify-content-between mb-4">
            <h2 className="m-0">Documents</h2>
            {hasAnyRole(['ADMIN', 'EDITOR']) && (
              <Button
                label="Upload Document"
                icon="pi pi-upload"
                onClick={() => navigate('/upload')}
              />
            )}
          </div>

          <Toast ref={toast} />
          <ConfirmDialog />

          {/* Filters */}
          <div className="grid mb-4">
            <div className="col-12 md:col-6 lg:col-4">
              <div className="field">
                <label className="block text-sm font-medium mb-2">Search</label>
                <InputText
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder="Search by title, code, or description"
                  className="w-full"
                />
              </div>
            </div>
            <div className="col-12 md:col-6 lg:col-4">
              <div className="field">
                <label className="block text-sm font-medium mb-2">Status</label>
                <Dropdown
                  value={filters.status}
                  options={statusOptions}
                  onChange={(e) => handleFilterChange('status', e.value)}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          {/* Documents Table */}
          <DataTable
            value={documents}
            paginator
            rows={filters.limit}
            totalRecords={pagination.total}
            lazy
            onPage={handlePageChange}
            onSort={handleSort}
            sortField={filters.sortBy}
            sortOrder={filters.sortOrder === 'ASC' ? 1 : -1}
            emptyMessage="No documents found"
            className="p-datatable-sm"
          >
            <Column
              field="title"
              header="Title"
              sortable
              style={{ minWidth: '200px' }}
            />
            <Column
              field="shortCode"
              header="Code"
              sortable
              style={{ width: '100px' }}
            />
            <Column
              field="status"
              header="Status"
              body={statusBodyTemplate}
              sortable
              style={{ width: '120px' }}
            />
            <Column
              field="fileSize"
              header="Size"
              body={(rowData) => formatFileSize(rowData.fileSize)}
              sortable
              style={{ width: '100px' }}
            />
            <Column
              field="uploader.firstName"
              header="Uploaded By"
              body={(rowData) => 
                rowData.uploader ? 
                  `${rowData.uploader.firstName} ${rowData.uploader.lastName}` : 
                  'Unknown'
              }
              sortable
              style={{ width: '150px' }}
            />
            <Column
              field="createdAt"
              header="Uploaded"
              body={(rowData) => formatDate(rowData.createdAt)}
              sortable
              style={{ width: '120px' }}
            />
            <Column
              header="Actions"
              body={actionBodyTemplate}
              style={{ width: '200px' }}
            />
          </DataTable>

          {/* Pagination Info */}
          <div className="flex justify-content-between align-items-center mt-3">
            <div className="text-sm text-muted">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
              {pagination.total} documents
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default DocumentListPage;